import { FileType, PersonalNotificationsCategoryType } from '@fi-shared/types';
import { FiDateFormatOptions } from '@fi-shared/utils';
import { MediaBankFileSetting } from './media-bank-file-setting';
import { IPublicationSetting, PublicationSettingModel } from './publication-setting.model';
import { AccessRightFilterCollectionModel } from './access-right-filter-collection.model';
import { IMediaBankFile } from '@fi-shared/media-bank';
import { MediaBankStorageType } from '@fi-shared/media-bank/media-bank-storage-type';
import { ILightWeightObject } from '@fi-shared/models/local-search.model';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { IPublicationItemBase } from '@fi-shared/models/models';

export class FiMediaBankFileModel implements IMediaBankFile, IPublicationItemBase {
    Id = 0;
    OrigId = 0;
    PhysicalFileName = '';
    Extension = '';
    Name = '';
    Description = '';
    Size = 0;
    FullName = '';

    Names: IContentLanguageLabelModel[] = [];
    Descriptions: IContentLanguageLabelModel[] = [];

    Width = 0;
    Height = 0;
    DPI = 0;
    ColorDepth = 0;
    ColorScale = '';

    TagIds = [];
    Tags: ILightWeightObject[] = [];

    FileType = FileType.Unknown;
    StorageType = MediaBankStorageType.Azure;

    IsFavorite = false;
    IsDeleted = false;
    Summary = '';
    Content: string;

    CreatedByUserId = 0;
    CreatedByUserName = '';
    ChangedByUserId = 0;
    ChangedByUserName = '';
    CreatedOn = new Date().toISOString();
    CreatedOnDate = '';
    ChangedOn = new Date().toISOString();
    ChangedOnDate = '';
    FileCreatedOn: string; //ISO 8601 Date
    CommentCount = 0;

    IsPublished = false;

    MediaBankFileSetting = new MediaBankFileSetting();
    PublicationSetting: IPublicationSetting = PublicationSettingModel.getNewInstance();
    AccessRightFilterCollection = new AccessRightFilterCollectionModel();

    // client side props
    Url = '';
    ContentType = 'application/octet-stream';
    NotificationCategory = PersonalNotificationsCategoryType.Visited;
    IsInUse = false;
    HasViewAccessAsSuperAdmin: boolean;
    SiteDesignIds: number[] = [];

    PublicationStartDate: string;
    PublicationSettingChangedByUserId: number;
    PublicationSettingChangedByUserName: string;

    constructor(fiFormatDateService: FiFormatDateService, data?: IMediaBankFile) {
        this.CreatedOnDate = fiFormatDateService.formatDate(this.CreatedOn, FiDateFormatOptions);
        this.ChangedOnDate = fiFormatDateService.formatDate(this.ChangedOn, FiDateFormatOptions);

        if (data) {
            this.Initialize(data, fiFormatDateService);
        }
    }

    Initialize(
        mediaBankFile: IMediaBankFile,
        fiFormatDateService: FiFormatDateService,
        args?: {
            preserveDescriptions?: boolean;
            preserveSettings?: boolean;
        },
    ) {
        if (!args) {
            args = {};
        }

        this.Id = (<any>mediaBankFile).MediaBankFileId || mediaBankFile.Id;

        if (!this.OrigId) {
            this.OrigId = this.Id;
        }
        this.PhysicalFileName = mediaBankFile.PhysicalFileName;
        this.Extension = mediaBankFile.Extension;
        this.IsDeleted = mediaBankFile.IsDeleted;

        if (!this.Name) {
            this.Name = mediaBankFile.Name;
        }
        if (mediaBankFile.Names) {
            this.Names = mediaBankFile.Names.slice();
        }

        this.Description = mediaBankFile.Description;
        if (mediaBankFile.Descriptions && !args.preserveDescriptions) {
            this.Descriptions = mediaBankFile.Descriptions.slice();
        }

        this.Size = mediaBankFile.Size;
        this.FullName = mediaBankFile.FullName;

        this.Width = mediaBankFile.Width;
        this.Height = mediaBankFile.Height;
        this.DPI = mediaBankFile.DPI;
        this.ColorDepth = mediaBankFile.ColorDepth;
        this.ColorScale = mediaBankFile.ColorScale;

        this.TagIds = mediaBankFile.TagIds;
        this.Tags = mediaBankFile.Tags;

        this.FileType = mediaBankFile.FileType;
        this.StorageType = mediaBankFile.StorageType;
        this.Summary = mediaBankFile.Summary;
        this.Content = mediaBankFile.Content;

        this.IsFavorite = mediaBankFile.IsFavorite;

        this.CreatedByUserId = mediaBankFile.CreatedByUserId;
        this.CreatedByUserName = mediaBankFile.CreatedByUserName;
        this.CreatedOn = mediaBankFile.CreatedOn;
        this.CreatedOnDate = fiFormatDateService.formatDate(this.CreatedOn, FiDateFormatOptions);
        this.ChangedByUserId = mediaBankFile.ChangedByUserId;
        this.ChangedByUserName = mediaBankFile.ChangedByUserName;
        this.ChangedOn = mediaBankFile.ChangedOn;
        this.ChangedOnDate = fiFormatDateService.formatDate(this.ChangedOn, FiDateFormatOptions);
        this.FileCreatedOn = mediaBankFile.FileCreatedOn;

        if (!args.preserveSettings) {
            if (mediaBankFile.MediaBankFileSetting != null) {
                this.MediaBankFileSetting.Initialize(mediaBankFile.MediaBankFileSetting);
            }

            this.PublicationStartDate = mediaBankFile.PublicationStartDate;
            this.PublicationSettingChangedByUserId = mediaBankFile.PublicationSettingChangedByUserId;
            this.PublicationSettingChangedByUserName = mediaBankFile.PublicationSettingChangedByUserName;

            this.PublicationSetting = mediaBankFile.PublicationSetting;

            if (mediaBankFile.AccessRightFilterCollection != null) {
                this.AccessRightFilterCollection.InitializeAccessRightFilterCollection(
                    mediaBankFile.AccessRightFilterCollection,
                );
            }
        }

        this.CommentCount = mediaBankFile.CommentCount || 0;

        if (mediaBankFile.Url) {
            this.Url = mediaBankFile.Url;
        }

        if (mediaBankFile.ContentType) {
            this.ContentType = mediaBankFile.ContentType;
        }

        this.IsPublished = mediaBankFile.IsPublished;

        this.NotificationCategory = mediaBankFile.NotificationCategory;
        this.IsInUse = mediaBankFile.IsInUse;
        this.HasViewAccessAsSuperAdmin = mediaBankFile.HasViewAccessAsSuperAdmin;
        this.SiteDesignIds = mediaBankFile.SiteDesignIds;
    }
}
